import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Users, DollarSign, Star, Calendar, Zap, ArrowRight } from "lucide-react";
import { api } from "../../lib/apiClient";
import { formatBizDateTimeLong } from "../../lib/formatBiz";
import { T } from "../../lib/testIds";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;

  const kpis = [
    { icon: Briefcase, label: "Jobs today", value: data.kpis.jobs_today, tid: T.dashboard.kpiJobsToday, tint: "bg-sky-50 text-[color:var(--owc-sky)]" },
    { icon: Users, label: "New leads", value: data.kpis.new_leads, tid: T.dashboard.kpiNewLeads, tint: "bg-emerald-50 text-emerald-600" },
    { icon: DollarSign, label: "Revenue MTD", value: `$${(data.kpis.revenue_mtd || 0).toLocaleString()}`, tid: T.dashboard.kpiRevenue, tint: "bg-amber-50 text-amber-600" },
    { icon: Star, label: "Reviews pending", value: data.kpis.pending_reviews, tid: T.dashboard.kpiPendingReviews, tint: "bg-fuchsia-50 text-fuchsia-600" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Dashboard</h1>
        <p className="text-slate-600 mt-1">Here's what's happening at Wonder Washers today.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} data-testid={k.tid} className="owc-card p-5 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-slate-500">{k.label}</div>
              <div className="mt-2 font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">{k.value}</div>
            </div>
            <span className={`h-11 w-11 rounded-lg flex items-center justify-center ${k.tint}`}><k.icon className="h-5 w-5" /></span>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="owc-card p-6" data-testid={T.dashboard.upcomingList}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] flex items-center gap-2"><Calendar className="h-4 w-4 text-[color:var(--owc-sky)]" /> Upcoming schedule</h2>
            <Link to="/admin/calendar" className="text-xs font-semibold text-[color:var(--owc-sky)] inline-flex items-center gap-1">See all <ArrowRight className="h-3 w-3" /></Link>
          </div>
          {data.upcoming.length === 0 ? (
            <div className="text-slate-500 text-sm">Nothing scheduled yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.upcoming.map(j => (
                <li key={j.id} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[color:var(--owc-navy)]">{j.service_name}</div>
                    <div className="text-xs text-slate-500">{j.contact_name} · {j.address}</div>
                  </div>
                  <div className="text-xs font-semibold owc-badge-blue">{formatWhen(j.scheduled_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="owc-card p-6" data-testid={T.dashboard.activityList}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] flex items-center gap-2"><Zap className="h-4 w-4 text-[color:var(--owc-sky)]" /> Recent automation activity</h2>
            <Link to="/admin/automations" className="text-xs font-semibold text-[color:var(--owc-sky)] inline-flex items-center gap-1">All logs <ArrowRight className="h-3 w-3" /></Link>
          </div>
          {data.recent_activity.length === 0 ? (
            <div className="text-slate-500 text-sm">No automation activity yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_activity.map(a => (
                <li key={a.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-[color:var(--owc-navy)] text-sm">{a.template_key.replaceAll("_", " ")}</div>
                    <StatusBadge status={a.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{a.channel.toUpperCase()} → {a.to}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: "owc-badge-green",
    stub: "owc-badge-slate",
    queued: "owc-badge-amber",
    failed: "owc-badge-red",
    suppressed: "owc-badge-red",
    received: "owc-badge-blue",
  };
  return <span className={map[status] || "owc-badge-slate"}>{status}</span>;
}

function formatWhen(iso) {
  return formatBizDateTimeLong(iso);
}
